import { EFinancingProgram, EOriginSystemId, EProvince } from '@if/core/types'
import {
  BaseWorksheetFormConfig,
  buildBaseWorksheetFormConfig,
  mergeWorksheetFormDefaults,
  useResolvedFinancingConfig,
  WorksheetSimulationPricing,
} from '@src/components/WorksheetForm'
import { useBaseWorksheetFormInitialValues } from '@src/components/WorksheetForm/BaseWorksheetForm.schema'
import {
  WorksheetFormDefaults,
  worksheetFormDefaultsQuery,
  worksheetFormDefaultsSimulationQuery,
} from '@src/data/api/computation/form-defaults-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { Constants } from '@src/data/types'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { NormCreditDecision } from '@src/data/types/CreditDecisionSchema'
import { Merchant } from '@src/data/types/Merchant'
import { QueryKey, useQueryClient } from '@tanstack/react-query'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'
import { CalculatorValues, CalculatorSchema, useCalculatorForm } from '../calculatorSchema'

export interface UseCalculatorFormSetupProps {
  creditApplicationId: string | null
  financingProgramId: EFinancingProgram | null
  merchantId: string | null
  expiresOn: Date | string | null
  createdOn: Date | null
  decisionInterestRate: number | null
  maxAmountFinanced: number | null
  maxTermDuration: number | null
  hasCoApplicant: boolean
  stateIso: EProvince | null
  normsCreditDecision: NormCreditDecision | null
  // Single-payment applications (e.g. MechanicalWarranty) force the payment frequency to Single.
  isSinglePayment?: boolean
}

export interface CalculatorFormSetup {
  config: BaseWorksheetFormConfig | null
  interestRate: number
  merchant: Merchant | null
}

// Data prep only — the form schema is built separately in useCalculatorFormFields, gated on config != null.
export const useCalculatorFormSetup = ({
  creditApplicationId,
  financingProgramId,
  merchantId,
  expiresOn,
  createdOn,
  decisionInterestRate,
  maxAmountFinanced,
  maxTermDuration,
  hasCoApplicant,
  stateIso,
  normsCreditDecision,
  isSinglePayment = false,
}: UseCalculatorFormSetupProps): CalculatorFormSetup => {
  const [merchant] = useGetMerchantById(merchantId ?? undefined)

  const programConfig =
    (financingProgramId && Constants.financingProgramConfigs[financingProgramId]) ||
    Constants.financingProgramConfigs.default

  const { financingConfig } = useResolvedFinancingConfig(financingProgramId ?? undefined, merchantId ?? undefined)

  const interestRate = decisionInterestRate ?? programConfig.minInterestRate

  const config = useMemo<BaseWorksheetFormConfig | null>(() => {
    if (!financingProgramId || !stateIso || !financingConfig || !createdOn || !expiresOn) {
      return null
    }
    return buildBaseWorksheetFormConfig({
      financingProgramId,
      creditApplicationId: creditApplicationId ?? nanoid(),
      merchantId: merchant?.id ?? '',
      financingConfig,
      stateIso,
      hasCoApplicant,
      createdOn,
      expiresOn,
      merchant,
      normsCreditDecision,
      maxAmountFinanced,
      maxTermDuration,
      isSinglePayment,
    })
  }, [
    financingProgramId,
    creditApplicationId,
    merchant,
    financingConfig,
    stateIso,
    hasCoApplicant,
    createdOn,
    expiresOn,
    normsCreditDecision,
    maxAmountFinanced,
    maxTermDuration,
    isSinglePayment,
  ])

  return {
    config,
    interestRate,
    merchant,
  }
}

export interface UseCalculatorFormFieldsArgs {
  config: BaseWorksheetFormConfig
  worksheet: BaseWorksheetDto | null
  requestedLoanAmount: number | null
  simulationPricing?: WorksheetSimulationPricing
}

export interface CalculatorFormFields {
  schema: CalculatorSchema
  defaultValues: CalculatorValues | (() => Promise<CalculatorValues>) | undefined
}

interface DefaultsQuery {
  queryKey: QueryKey
  queryFn: () => Promise<WorksheetFormDefaults>
}

// The backend-defaults query, or undefined when the form must not be seeded — editing an
// existing worksheet, or missing an input a route requires.
const buildDefaultsQuery = (
  simulationPricing: WorksheetSimulationPricing | undefined,
  config: BaseWorksheetFormConfig,
  worksheet: BaseWorksheetDto | null,
  requestedLoanAmount: number | null,
): DefaultsQuery | undefined => {
  if (simulationPricing) {
    if (!config.merchantId || requestedLoanAmount == null || requestedLoanAmount <= 0) return undefined
    return worksheetFormDefaultsSimulationQuery(config.financingProgramId, {
      merchantId: config.merchantId,
      stateIso: config.stateIso,
      amountRequested: requestedLoanAmount,
      interestRate: simulationPricing.interestRate,
      maxPmtAmount: simulationPricing.maxPmtAmount,
      hasCoapplicant: config.hasCoApplicant,
      includeInsurance: false,
      originSystemId: EOriginSystemId.Backoffice,
    })
  }

  if (worksheet || !config.creditApplicationId) return undefined
  return worksheetFormDefaultsQuery(config.financingProgramId, {
    creditApplicationId: config.creditApplicationId,
    originSystemId: EOriginSystemId.Backoffice,
  })
}

export const useCalculatorFormFields = ({
  config,
  worksheet,
  requestedLoanAmount,
  simulationPricing,
}: UseCalculatorFormFieldsArgs): CalculatorFormFields => {
  const { schema, defaultValues } = useCalculatorForm(config)
  const initialValues = useBaseWorksheetFormInitialValues(defaultValues, worksheet, requestedLoanAmount)
  const queryClient = useQueryClient()

  const defaultsQuery = buildDefaultsQuery(simulationPricing, config, worksheet, requestedLoanAmount)
  if (!defaultsQuery || !initialValues) {
    return { schema, defaultValues: initialValues }
  }

  const seed = initialValues
  return {
    schema,
    defaultValues: async () => mergeWorksheetFormDefaults(seed, await queryClient.fetchQuery(defaultsQuery)),
  }
}
